(() => {
  if (window.__quarkGlobalAAP) {
    return;
  }

  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const MORPH_DURATION = 360;
  const LABEL_DURATION = 320;
  const BOUNCE_DURATION = 420;
  const SOURCE_SWITCH_OUT_DURATION = 220;

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function escapeSelectorValue(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }

    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  function normalizeModalKey(value) {
    if (typeof value !== 'string') {
      return '';
    }

    return value.trim().toLowerCase();
  }

  class QuarkGlobalAAP {
    constructor() {
      this.states = new Map();
      this.activeState = null;
      this.labelSwapTimer = null;
      this.sourceSwitchTimer = null;
      this.metricsRaf = null;
      this.metricsSignature = '';
      this.appliedThemeKeys = [];
      this.modalOpen = false;
      this.lastFocusedElement = null;
      this.isDocked = false;
      this.dockedSource = null;
      this.isVisible = false;
      this.reducedMotionMedia = window.matchMedia(REDUCED_MOTION_QUERY);
      this.reducedMotion = this.reducedMotionMedia.matches;
      this.transportDotCount = 0;
      this.transportProgressToken = -1;

      this.handleStateEvent = this.handleStateEvent.bind(this);
      this.handleButtonClick = this.handleButtonClick.bind(this);
      this.handleButtonKeydown = this.handleButtonKeydown.bind(this);
      this.handleModalClick = this.handleModalClick.bind(this);
      this.handleModalAfterHide = this.handleModalAfterHide.bind(this);
      this.handleMotionChanged = this.handleMotionChanged.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.handleButtonResize = this.handleButtonResize.bind(this);

      this.buildDom();
      this.bind();
      this.emitMetrics();
    }

    buildDom() {
      this.root = document.createElement('div');
      this.root.className = 'qga-aap';
      this.root.hidden = true;

      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.className = 'qga-aap__button';
      this.button.setAttribute('aria-disabled', 'true');
      this.button.setAttribute('aria-label', 'More details');

      this.bubble = document.createElement('span');
      this.bubble.className = 'qga-aap__bubble';
      this.bubble.setAttribute('aria-hidden', 'true');

      this.content = document.createElement('span');
      this.content.className = 'qga-aap__content';

      this.labelSlot = document.createElement('span');
      this.labelSlot.className = 'qga-aap__label-slot';

      this.label = document.createElement('span');
      this.label.className = 'qga-aap__label';
      this.label.textContent = '';
      this.labelSlot.appendChild(this.label);

      this.icon = document.createElement('span');
      this.icon.className = 'qga-aap__icon';
      this.icon.setAttribute('aria-hidden', 'true');
      this.icon.textContent = '+';

      this.transport = document.createElement('span');
      this.transport.className = 'qga-aap__transport';
      this.transport.setAttribute('aria-hidden', 'true');

      this.transportPlay = document.createElement('span');
      this.transportPlay.className = 'qga-aap__transport-play';
      this.transportPlay.setAttribute('data-state', 'play');

      this.transportPlay.innerHTML = `
        <svg class="qga-aap__transport-icon qga-aap__transport-icon--play" viewBox="0 0 56 56" aria-hidden="true"><path d="m23.7555 36.6237c.4478 0 .8598-.1343 1.4241-.4568l10.9178-6.3322c.8598-.5016 1.3614-1.021 1.3614-1.8361 0-.8061-.5016-1.3255-1.3614-1.8271l-10.9178-6.3322c-.5643-.3314-.9762-.4657-1.4241-.4657-.9315 0-1.7555.7165-1.7555 1.9435v13.3629c0 1.227.824 1.9435 1.7555 1.9435z"></path></svg>
        <svg class="qga-aap__transport-icon qga-aap__transport-icon--pause" viewBox="0 0 56 56" aria-hidden="true"><path d="m21.7334 36.67h2.5342c1.1483 0 1.7324-.5796 1.7324-1.7193v-13.9015c0-1.12-.5841-1.6898-1.7324-1.7193h-2.5342c-1.1483 0-1.7324.5698-1.7324 1.7193v13.9015c-.0297 1.1396.5544 1.7193 1.7324 1.7193zm9.9992 0h2.5347c1.1485 0 1.7327-.5796 1.7327-1.7193v-13.9015c0-1.12-.5842-1.7193-1.7327-1.7193h-2.5347c-1.1485 0-1.7327.5698-1.7327 1.7193v13.9015c0 1.1396.5545 1.7193 1.7327 1.7193z"></path></svg>
        <svg class="qga-aap__transport-icon qga-aap__transport-icon--replay" viewBox="0 0 56 56" aria-hidden="true"><path d="m36.2448 26.6447c-1.1073 0-2.0052.8978-2.0052 2.0052 0 3.4405-2.7992 6.2397-6.2397 6.2397s-6.2397-2.7992-6.2397-6.2397 2.7992-6.2397 6.2397-6.2397c.0283 0 .0546-.0072.0825-.0083l-1.2839 1.2841c-.7833.7828-.7833 2.0526 0 2.8354.3911.3916.9047.5874 1.4177.5874s1.0266-.1958 1.4177-.5874l4.4406-4.4406c.7833-.7828.7833-2.0526 0-2.8354l-4.657-4.657c-.7823-.7833-2.0531-.7833-2.8354 0-.7833.7828-.7833 2.0526 0 2.8354l.9973.9974c-5.4561.223-9.8295 4.7189-9.8295 10.2287 0 5.6517 4.5983 10.25 10.25 10.25s10.25-4.5983 10.25-10.25c0-1.1073-.8978-2.0052-2.0052-2.0052z"></path></svg>
      `;

      this.transportDots = document.createElement('span');
      this.transportDots.className = 'qga-aap__transport-dots';

      this.transport.append(this.transportPlay, this.transportDots);

      this.nav = document.createElement('span');
      this.nav.className = 'qga-aap__nav';
      this.nav.setAttribute('aria-hidden', 'true');

      this.navPrev = document.createElement('span');
      this.navPrev.className = 'qga-aap__nav-arrow qga-aap__nav-arrow--prev';
      this.navPrev.textContent = '‹';

      this.navNext = document.createElement('span');
      this.navNext.className = 'qga-aap__nav-arrow qga-aap__nav-arrow--next';
      this.navNext.textContent = '›';

      this.nav.append(this.navPrev, this.navNext);

      this.content.append(this.labelSlot, this.nav, this.icon, this.transport);
      this.button.append(this.bubble, this.content);
      this.root.appendChild(this.button);
      document.body.appendChild(this.root);

      this.modal = document.createElement('x-drawer');
      this.modal.id = `qga-modal-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
      this.modal.className = 'drawer qga-modal-drawer';
      this.modal.setAttribute('open-from', 'bottom');
      this.modal.setAttribute('aria-label', 'More details');

      this.modalSheet = document.createElement('div');
      this.modalSheet.className = 'qga-modal__sheet';

      this.modalClose = document.createElement('button');
      this.modalClose.type = 'button';
      this.modalClose.className = 'qga-modal__close';
      this.modalClose.setAttribute('data-qga-close', 'true');
      this.modalClose.setAttribute('aria-label', 'Close details');
      this.modalClose.innerHTML = `
        <span class="qga-modal__close-icon" aria-hidden="true">
          <svg viewBox="0 0 36 36" focusable="false" aria-hidden="true">
            <path d="m20.1211 18 3.4395-3.4395c.5859-.5854.5859-1.5356 0-2.1211-.5859-.5859-1.5352-.5859-2.1211 0l-3.4395 3.4395-3.4395-3.4395c-.5859-.5859-1.5352-.5859-2.1211 0-.5859.5854-.5859 1.5356 0 2.1211l3.4395 3.4395-3.4395 3.4395c-.5859.5854-.5859 1.5356 0 2.1211.293.293.6768.4395 1.0605.4395s.7676-.1465 1.0605-.4395l3.4395-3.4395 3.4395 3.4395c.293.293.6768.4395 1.0605.4395s.7676-.1465 1.0605-.4395c.5859-.5854.5859-1.5356 0-2.1211l-3.4395-3.4395z"></path>
          </svg>
        </span>
      `;

      this.modalBody = document.createElement('div');
      this.modalBody.className = 'qga-modal__body';
      this.modalBody.setAttribute('data-qga-modal-body', 'true');

      this.modalSheet.append(this.modalClose, this.modalBody);
      this.modal.append(this.modalSheet);
      document.body.appendChild(this.modal);

      if (window.ResizeObserver) {
        this.buttonResizeObserver = new ResizeObserver(this.handleButtonResize);
        this.buttonResizeObserver.observe(this.button);
      }
    }

    bind() {
      document.addEventListener('quark:aap:state', this.handleStateEvent);
      this.button.addEventListener('click', this.handleButtonClick);
      this.button.addEventListener('keydown', this.handleButtonKeydown);
      this.modal.addEventListener('click', this.handleModalClick);
      this.modal.addEventListener('dialog:after-hide', this.handleModalAfterHide);
      window.addEventListener('scroll', this.handleViewportChange, { passive: true });
      window.addEventListener('resize', this.handleViewportChange, { passive: true });

      if (typeof this.reducedMotionMedia.addEventListener === 'function') {
        this.reducedMotionMedia.addEventListener('change', this.handleMotionChanged);
      } else if (typeof this.reducedMotionMedia.addListener === 'function') {
        this.reducedMotionMedia.addListener(this.handleMotionChanged);
      }
    }

    handleButtonResize() {
      this.scheduleMetrics();
    }

    handleMotionChanged(event) {
      this.reducedMotion = Boolean(event.matches);
    }

    handleViewportChange() {
      if (this.isDocked) {
        this.updateDockPosition();
      }

      this.scheduleMetrics();
    }

    handleStateEvent(event) {
      const detail = event && event.detail ? event.detail : null;
      if (!detail || !detail.sourceId) {
        return;
      }

      if (detail.removed) {
        this.states.delete(detail.sourceId);
        this.syncActiveState();
        return;
      }

      const previous = this.states.get(detail.sourceId) || {};
      const incomingPlayState = typeof detail.transportPlayState === 'string'
        ? detail.transportPlayState
        : (typeof previous.transportPlayState === 'string' ? previous.transportPlayState : 'play');
      const transportPlayState = incomingPlayState === 'pause' || incomingPlayState === 'replay' ? incomingPlayState : 'play';
      const transportSlideCount = Math.max(
        0,
        Number.isFinite(Number(detail.transportSlideCount))
          ? Number(detail.transportSlideCount)
          : (Number.isFinite(Number(previous.transportSlideCount)) ? Number(previous.transportSlideCount) : 0)
      );
      const fallbackTransportIndex = Number.isFinite(Number(previous.transportActiveIndex))
        ? Number(previous.transportActiveIndex)
        : 0;
      const parsedTransportIndex = Number.isFinite(Number(detail.transportActiveIndex))
        ? Number(detail.transportActiveIndex)
        : fallbackTransportIndex;
      const maxTransportIndex = Math.max(0, transportSlideCount - 1);
      const transportActiveIndex = Math.min(maxTransportIndex, Math.max(0, parsedTransportIndex));
      const nextState = {
        sourceId: detail.sourceId,
        sourceEl: detail.sourceEl || previous.sourceEl || document.querySelector(`[data-qss-source-id="${escapeSelectorValue(detail.sourceId)}"]`),
        visible: Boolean(detail.visible),
        priority: Number.isFinite(Number(detail.priority)) ? Number(detail.priority) : 0,
        ratio: clamp(Number(detail.ratio) || 0, 0, 1),
        panelIndex: Number.isFinite(Number(detail.panelIndex)) ? Number(detail.panelIndex) : 0,
        ctaLabel: typeof detail.ctaLabel === 'string' ? detail.ctaLabel.trim() : '',
        fallbackCtaLabel: typeof detail.fallbackCtaLabel === 'string'
          ? detail.fallbackCtaLabel.trim()
          : (typeof previous.fallbackCtaLabel === 'string' ? previous.fallbackCtaLabel : ''),
        modalKey: normalizeModalKey(detail.modalKey),
        navEnabled: Boolean(detail.navEnabled),
        navPrevDisabled: Boolean(detail.navPrevDisabled),
        navNextDisabled: Boolean(detail.navNextDisabled),
        transportEnabled: Boolean(detail.transportEnabled),
        transportPlayState,
        transportDisabled: Boolean(detail.transportDisabled),
        transportSlideCount,
        transportActiveIndex,
        transportProgressEnabled: Boolean(detail.transportProgressEnabled),
        transportProgressDuration: typeof detail.transportProgressDuration === 'string' ? detail.transportProgressDuration : (typeof previous.transportProgressDuration === 'string' ? previous.transportProgressDuration : ''),
        transportProgressPlayState: typeof detail.transportProgressPlayState === 'string' ? detail.transportProgressPlayState : (typeof previous.transportProgressPlayState === 'string' ? previous.transportProgressPlayState : 'paused'),
        transportProgressToken: Number.isFinite(Number(detail.transportProgressToken))
          ? Number(detail.transportProgressToken)
          : (Number.isFinite(Number(previous.transportProgressToken)) ? Number(previous.transportProgressToken) : 0),
        dockToSource: Boolean(detail.dockToSource),
        dockTargetEl: detail.dockTargetEl || previous.dockTargetEl || null,
        aapTheme: detail.aapTheme && typeof detail.aapTheme === 'object' ? detail.aapTheme : previous.aapTheme,
        updatedAt: Date.now()
      };

      this.states.set(detail.sourceId, nextState);
      this.syncActiveState();
    }

    syncActiveState() {
      const candidates = Array.from(this.states.values()).filter(
        (state) => state.visible && (state.ctaLabel || state.navEnabled || state.transportEnabled)
      );

      if (!candidates.length) {
        this.activeState = null;
        this.applyDocking(false, null);
        this.hideAAP();
        this.closeModal();
        return;
      }

      candidates.sort((a, b) => {
        if (a.priority !== b.priority) {
          return b.priority - a.priority;
        }

        if (a.ratio !== b.ratio) {
          return b.ratio - a.ratio;
        }

        return b.updatedAt - a.updatedAt;
      });

      const nextActive = candidates[0];
      const previousActive = this.activeState;
      const hasChanged = !previousActive
        || previousActive.sourceId !== nextActive.sourceId
        || previousActive.panelIndex !== nextActive.panelIndex
        || previousActive.ctaLabel !== nextActive.ctaLabel
        || previousActive.fallbackCtaLabel !== nextActive.fallbackCtaLabel
        || previousActive.modalKey !== nextActive.modalKey
        || previousActive.navEnabled !== nextActive.navEnabled
        || previousActive.navPrevDisabled !== nextActive.navPrevDisabled
        || previousActive.navNextDisabled !== nextActive.navNextDisabled
        || previousActive.transportEnabled !== nextActive.transportEnabled
        || previousActive.transportDisabled !== nextActive.transportDisabled
        || previousActive.transportPlayState !== nextActive.transportPlayState
        || previousActive.transportSlideCount !== nextActive.transportSlideCount
        || previousActive.transportActiveIndex !== nextActive.transportActiveIndex
        || previousActive.transportProgressEnabled !== nextActive.transportProgressEnabled
        || previousActive.transportProgressDuration !== nextActive.transportProgressDuration
        || previousActive.transportProgressPlayState !== nextActive.transportProgressPlayState
        || previousActive.transportProgressToken !== nextActive.transportProgressToken;
      const sourceChanged = !previousActive || previousActive.sourceId !== nextActive.sourceId;

      this.activeState = nextActive;
      this.applyTheme(nextActive.aapTheme);
      const shouldAnimateOutIn = Boolean(
        sourceChanged
        && previousActive
        && !this.shouldMorphDirectlyBetweenSources(previousActive, nextActive)
      );

      const shouldDock = Boolean(nextActive.dockToSource) && candidates.length === 1;
      this.applyDocking(shouldDock, nextActive.dockTargetEl || nextActive.sourceEl);

      if (shouldAnimateOutIn) {
        this.transitionSourceSwitch(nextActive, { hasChanged });
      } else {
        this.showAAP();
        this.applyPresentation(nextActive, {
          hasChanged,
          animateLabel: true,
          forceStaticLabel: false
        });
      }

      if (this.modalOpen && hasChanged) {
        this.closeModal();
      }

      this.scheduleMetrics();
    }

    getEffectiveCtaLabel(state) {
      if (!state) {
        return '';
      }

      return (state.ctaLabel || state.fallbackCtaLabel || '').trim();
    }

    applyPresentation(state, options) {
      if (!state) {
        return;
      }

      const opts = {
        hasChanged: false,
        animateLabel: true,
        forceStaticLabel: false,
        ...options
      };
      const transportMode = Boolean(state.transportEnabled);
      const navMode = !transportMode && Boolean(state.navEnabled);
      const effectiveCtaLabel = this.getEffectiveCtaLabel(state);

      this.button.classList.toggle('is-transport-mode', transportMode);
      this.button.classList.toggle('is-nav-mode', navMode);
      this.navPrev.classList.toggle('is-disabled', !navMode || state.navPrevDisabled);
      this.navNext.classList.toggle('is-disabled', !navMode || state.navNextDisabled);

      if (transportMode) {
        this.updateTransportState(state, { hasChanged: opts.hasChanged });
      }

      if (transportMode || navMode) {
        this.clearLabelSwap();
        this.setStaticLabel('');
        this.button.style.removeProperty('--qga-target-width');
        this.button.classList.remove('is-bouncing');
      } else if (effectiveCtaLabel) {
        if (opts.forceStaticLabel || !opts.animateLabel) {
          this.clearLabelSwap();
          this.setStaticLabel(effectiveCtaLabel);
          this.button.style.removeProperty('--qga-target-width');
          this.button.classList.remove('is-bouncing');
        } else if (opts.hasChanged) {
          this.updateLabel(effectiveCtaLabel);
        } else if (!this.label || !this.label.textContent) {
          this.setStaticLabel(effectiveCtaLabel);
        }
      }

      const sourceEl = state.sourceEl || document.querySelector(`[data-qss-source-id="${escapeSelectorValue(state.sourceId)}"]`);
      const modalEnabled = Boolean(state.modalKey) && Boolean(this.findModalTemplate(sourceEl, state.modalKey));
      const navDisabled = !navMode || (state.navPrevDisabled && state.navNextDisabled);
      let disabled;
      if (transportMode) {
        disabled = Boolean(state.transportDisabled);
      } else if (navMode) {
        disabled = navDisabled;
      } else {
        disabled = !modalEnabled;
      }

      this.button.setAttribute('aria-disabled', disabled ? 'true' : 'false');

      if (transportMode) {
        const transportState = state.transportPlayState || 'play';
        if (disabled) {
          this.button.setAttribute('aria-label', 'Animation unavailable');
        } else if (transportState === 'pause') {
          this.button.setAttribute('aria-label', 'Pause current animation');
        } else if (transportState === 'replay') {
          this.button.setAttribute('aria-label', 'Replay current animation');
        } else {
          this.button.setAttribute('aria-label', 'Play current animation');
        }
      } else if (navMode) {
        this.button.setAttribute('aria-label', 'Carousel navigation');
      } else {
        const label = effectiveCtaLabel || 'Learn more';
        this.button.setAttribute('aria-label', disabled ? label : `${label}. Open details`);
      }
    }

    applyDocking(shouldDock, sourceEl) {
      this.isDocked = Boolean(shouldDock && sourceEl);
      this.dockedSource = this.isDocked ? sourceEl : null;
      this.root.classList.toggle('is-docked', this.isDocked);

      if (!this.isDocked) {
        this.root.style.removeProperty('--qga-bottom');
        this.scheduleMetrics();
        return;
      }

      this.updateDockPosition();
    }

    getBaseBottom() {
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 1440;
      if (viewportWidth <= 734) {
        return 10;
      }

      return Math.round(clamp(viewportWidth * 0.018, 12, 28));
    }

    updateDockPosition() {
      if (!this.dockedSource) {
        return;
      }

      const rect = this.dockedSource.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const baseBottom = this.getBaseBottom();
      const trailingDelta = Math.max(0, viewportHeight - rect.bottom);
      const bottom = baseBottom + trailingDelta;
      this.root.style.setProperty('--qga-bottom', `${Math.round(bottom)}px`);
      this.scheduleMetrics();
    }

    applyTheme(theme) {
      this.appliedThemeKeys.forEach((key) => {
        this.button.style.removeProperty(key);
      });

      this.appliedThemeKeys = [];

      if (!theme || typeof theme !== 'object') {
        return;
      }

      Object.keys(theme).forEach((key) => {
        const value = theme[key];
        if (typeof value !== 'string' || !value) {
          return;
        }

        this.button.style.setProperty(key, value);
        this.appliedThemeKeys.push(key);
      });
    }

    clearSourceSwitchTimer() {
      if (this.sourceSwitchTimer) {
        window.clearTimeout(this.sourceSwitchTimer);
        this.sourceSwitchTimer = null;
      }
    }

    getSourceElement(state) {
      if (!state) {
        return null;
      }

      if (state.sourceEl && state.sourceEl.nodeType === 1) {
        return state.sourceEl;
      }

      if (!state.sourceId) {
        return null;
      }

      return document.querySelector(`[data-qss-source-id="${escapeSelectorValue(state.sourceId)}"]`);
    }

    getSourceSection(state) {
      const sourceEl = this.getSourceElement(state);
      if (!sourceEl) {
        return null;
      }

      return sourceEl.closest('.shopify-section') || sourceEl;
    }

    shouldMorphDirectlyBetweenSources(previousState, nextState) {
      const previousSection = this.getSourceSection(previousState);
      const nextSection = this.getSourceSection(nextState);

      if (!previousSection || !nextSection) {
        return false;
      }

      if (previousSection === nextSection) {
        return true;
      }
      const sectionNodes = Array.from(document.querySelectorAll('.shopify-section'));
      const previousIndex = sectionNodes.indexOf(previousSection);
      const nextIndex = sectionNodes.indexOf(nextSection);

      if (previousIndex >= 0 && nextIndex >= 0) {
        return nextIndex === previousIndex + 1;
      }

      return false;
    }

    transitionSourceSwitch(nextState, options) {
      const opts = {
        hasChanged: false,
        ...options
      };

      this.clearSourceSwitchTimer();
      this.clearLabelSwap();
      this.button.style.removeProperty('--qga-target-width');
      this.button.classList.remove('is-bouncing');

      this.root.hidden = false;
      this.root.classList.remove('is-visible');
      this.isVisible = false;
      this.scheduleMetrics();

      const delay = this.reducedMotion ? 0 : SOURCE_SWITCH_OUT_DURATION;
      this.sourceSwitchTimer = window.setTimeout(() => {
        this.sourceSwitchTimer = null;
        this.applyPresentation(nextState, {
          hasChanged: opts.hasChanged,
          animateLabel: false,
          forceStaticLabel: true
        });
        this.root.hidden = false;
        requestAnimationFrame(() => {
          this.root.classList.add('is-visible');
          this.isVisible = true;
          this.scheduleMetrics();
        });
      }, delay);
    }

    showAAP() {
      this.clearSourceSwitchTimer();

      if (this.root.hidden) {
        this.root.hidden = false;
      }

      this.isVisible = true;
      requestAnimationFrame(() => {
        this.root.classList.add('is-visible');
        this.scheduleMetrics();
      });
    }

    hideAAP() {
      this.clearSourceSwitchTimer();
      this.root.classList.remove('is-visible');
      this.root.hidden = true;
      this.isVisible = false;
      this.clearLabelSwap();
      this.labelSlot.innerHTML = '';
      this.label = document.createElement('span');
      this.label.className = 'qga-aap__label';
      this.label.textContent = '';
      this.labelSlot.appendChild(this.label);
      this.button.style.removeProperty('--qga-target-width');
      this.button.classList.remove('is-bouncing');
      this.button.classList.remove('is-nav-mode');
      this.button.classList.remove('is-transport-mode');
      this.navPrev.classList.remove('is-disabled');
      this.navNext.classList.remove('is-disabled');
      this.transportPlay.classList.remove('is-disabled');
      this.transportPlay.setAttribute('data-state', 'play');
      this.transportDots.innerHTML = '';
      this.transportDotCount = 0;
      this.transportProgressToken = -1;
      this.button.style.removeProperty('--qga-transport-progress-duration');
      this.button.style.removeProperty('--qga-transport-progress-play-state');
      this.scheduleMetrics();
    }

    clearLabelSwap() {
      if (this.labelSwapTimer) {
        window.clearTimeout(this.labelSwapTimer);
        this.labelSwapTimer = null;
      }
    }

    measureButtonWidthForLabel(labelText) {
      const probe = this.button.cloneNode(true);
      const probeSlot = probe.querySelector('.qga-aap__label-slot');
      if (probeSlot) {
        probeSlot.innerHTML = '';
        const probeLabel = document.createElement('span');
        probeLabel.className = 'qga-aap__label';
        probeLabel.textContent = labelText;
        probeSlot.appendChild(probeLabel);
      }

      probe.classList.remove('is-bouncing');
      probe.style.position = 'fixed';
      probe.style.left = '-10000px';
      probe.style.top = '-10000px';
      probe.style.opacity = '0';
      probe.style.visibility = 'hidden';
      probe.style.pointerEvents = 'none';
      probe.style.transform = 'none';
      probe.style.removeProperty('--qga-target-width');
      probe.style.width = 'auto';

      document.body.appendChild(probe);
      const width = Math.ceil(probe.getBoundingClientRect().width);
      probe.remove();
      return width;
    }

    setStaticLabel(text) {
      this.labelSlot.innerHTML = '';
      this.label = document.createElement('span');
      this.label.className = 'qga-aap__label';
      this.label.textContent = text;
      this.labelSlot.appendChild(this.label);
    }

    updateLabel(text) {
      const safeText = typeof text === 'string' ? text : '';
      if (!safeText) {
        return;
      }

      let currentText = this.label && this.label.textContent ? this.label.textContent : '';
      const transitioningIncoming = this.labelSlot.querySelector('.qga-aap__label--in');
      const transitioningOutgoing = this.labelSlot.querySelector('.qga-aap__label--out');
      if (transitioningIncoming && transitioningIncoming.textContent) {
        currentText = transitioningIncoming.textContent;
      } else if (transitioningOutgoing && transitioningOutgoing.textContent) {
        currentText = transitioningOutgoing.textContent;
      }

      if (!currentText || this.reducedMotion) {
        this.setStaticLabel(safeText);
        this.button.style.removeProperty('--qga-target-width');
        this.button.classList.remove('is-bouncing');
        this.scheduleMetrics();
        return;
      }

      if (currentText === safeText) {
        return;
      }

      this.clearLabelSwap();
      this.button.classList.remove('is-bouncing');
      this.button.style.removeProperty('--qga-target-width');
      this.setStaticLabel(currentText);

      const oldWidth = Math.ceil(this.button.getBoundingClientRect().width);
      const newWidth = this.measureButtonWidthForLabel(safeText);

      const outgoing = document.createElement('span');
      outgoing.className = 'qga-aap__label qga-aap__label--out';
      outgoing.textContent = currentText;

      const incoming = document.createElement('span');
      incoming.className = 'qga-aap__label qga-aap__label--in';
      incoming.textContent = safeText;

      this.button.style.setProperty('--qga-target-width', `${oldWidth}px`);
      this.button.classList.remove('is-bouncing');
      void this.button.offsetWidth;
      this.button.classList.add('is-bouncing');

      this.labelSlot.innerHTML = '';
      this.labelSlot.append(outgoing, incoming);

      requestAnimationFrame(() => {
        this.button.style.setProperty('--qga-target-width', `${newWidth}px`);
        this.scheduleMetrics();
      });

      this.labelSwapTimer = window.setTimeout(() => {
        this.setStaticLabel(safeText);
        this.button.style.removeProperty('--qga-target-width');
        this.button.classList.remove('is-bouncing');
        this.labelSwapTimer = null;
        this.scheduleMetrics();
      }, this.reducedMotion ? 0 : Math.max(MORPH_DURATION, LABEL_DURATION, BOUNCE_DURATION) + 24);
    }

    emitTransportToggle() {
      if (!this.activeState) {
        return;
      }

      document.dispatchEvent(new CustomEvent('quark:aap:transport-toggle', {
        detail: {
          sourceId: this.activeState.sourceId
        }
      }));
    }

    emitTransportSeek(index) {
      if (!this.activeState || !Number.isFinite(Number(index))) {
        return;
      }

      document.dispatchEvent(new CustomEvent('quark:aap:transport-seek', {
        detail: {
          sourceId: this.activeState.sourceId,
          index: Number(index)
        }
      }));
    }

    rebuildTransportDots(count) {
      const safeCount = Math.max(0, Number(count) || 0);
      this.transportDots.innerHTML = '';
      this.transportDotCount = safeCount;

      for (let i = 0; i < safeCount; i += 1) {
        const dot = document.createElement('span');
        dot.className = 'qga-aap__transport-dot';
        dot.setAttribute('data-index', String(i));
        this.transportDots.appendChild(dot);
      }
    }

    applyTransportDotState(index, progressEnabled) {
      const dots = Array.from(this.transportDots.querySelectorAll('.qga-aap__transport-dot'));
      dots.forEach((dot, dotIndex) => {
        const isCurrent = dotIndex === index;
        dot.classList.toggle('is-current', isCurrent);
        dot.classList.remove('is-progressing');
      });

      if (!progressEnabled) {
        return;
      }

      const currentDot = dots[index];
      if (!currentDot) {
        return;
      }

      currentDot.classList.remove('is-progressing');
      void currentDot.offsetWidth;
      currentDot.classList.add('is-progressing');
    }

    updateTransportState(state, options) {
      const opts = {
        hasChanged: false,
        ...options
      };

      if (!state || !state.transportEnabled) {
        return;
      }

      const dotCount = Math.max(0, Number(state.transportSlideCount) || 0);
      if (dotCount !== this.transportDotCount) {
        this.rebuildTransportDots(dotCount);
      }

      const activeIndex = Math.min(Math.max(0, Number(state.transportActiveIndex) || 0), Math.max(0, dotCount - 1));
      const playState = state.transportPlayState === 'pause' || state.transportPlayState === 'replay'
        ? state.transportPlayState
        : 'play';
      const progressEnabled = Boolean(state.transportProgressEnabled);
      const progressDuration = state.transportProgressDuration || '4000ms';
      const progressPlayState = state.transportProgressPlayState === 'running' ? 'running' : 'paused';
      const token = Number.isFinite(Number(state.transportProgressToken)) ? Number(state.transportProgressToken) : 0;

      this.transportPlay.setAttribute('data-state', playState);
      this.transportPlay.classList.toggle('is-disabled', Boolean(state.transportDisabled));
      this.button.style.setProperty('--qga-transport-progress-duration', progressDuration);
      this.button.style.setProperty('--qga-transport-progress-play-state', progressPlayState);

      if (opts.hasChanged || token !== this.transportProgressToken) {
        this.transportProgressToken = token;
        this.applyTransportDotState(activeIndex, progressEnabled);
      } else {
        const dots = Array.from(this.transportDots.querySelectorAll('.qga-aap__transport-dot'));
        dots.forEach((dot, dotIndex) => {
          dot.classList.toggle('is-current', dotIndex === activeIndex);
          if (!progressEnabled) {
            dot.classList.remove('is-progressing');
          }
        });
      }
    }

    emitNavigate(direction) {
      if (!this.activeState || !direction) {
        return;
      }

      document.dispatchEvent(new CustomEvent('quark:aap:navigate', {
        detail: {
          sourceId: this.activeState.sourceId,
          direction
        }
      }));
    }

    handleButtonClick(event) {
      if (!this.activeState || this.button.getAttribute('aria-disabled') === 'true') {
        return;
      }

      if (this.activeState.transportEnabled) {
        const dotTarget = event && event.target && event.target.closest
          ? event.target.closest('.qga-aap__transport-dot')
          : null;
        if (dotTarget) {
          this.emitTransportSeek(dotTarget.dataset.index);
          return;
        }

        this.emitTransportToggle();
        return;
      }

      if (this.activeState.navEnabled) {
        const navPrevTarget = event && event.target && event.target.closest ? event.target.closest('.qga-aap__nav-arrow--prev') : null;
        const navNextTarget = event && event.target && event.target.closest ? event.target.closest('.qga-aap__nav-arrow--next') : null;
        if (navPrevTarget && !this.activeState.navPrevDisabled) {
          this.emitNavigate('prev');
          return;
        }

        if (navNextTarget && !this.activeState.navNextDisabled) {
          this.emitNavigate('next');
          return;
        }

        const rect = this.button.getBoundingClientRect();
        const clickX = rect.width > 0 ? ((event && Number.isFinite(event.clientX)) ? (event.clientX - rect.left) : rect.width / 2) : 0;
        let direction = clickX < (rect.width / 2) ? 'prev' : 'next';

        if (direction === 'prev' && this.activeState.navPrevDisabled && !this.activeState.navNextDisabled) {
          direction = 'next';
        } else if (direction === 'next' && this.activeState.navNextDisabled && !this.activeState.navPrevDisabled) {
          direction = 'prev';
        }

        if ((direction === 'prev' && this.activeState.navPrevDisabled) || (direction === 'next' && this.activeState.navNextDisabled)) {
          return;
        }

        this.emitNavigate(direction);
        return;
      }

      if (!this.activeState.modalKey) {
        return;
      }

      this.openModal();
    }

    handleButtonKeydown(event) {
      if (!this.activeState || this.button.getAttribute('aria-disabled') === 'true') {
        return;
      }

      if (this.activeState.transportEnabled) {
        const count = Math.max(0, Number(this.activeState.transportSlideCount) || 0);
        const index = Math.min(Math.max(0, Number(this.activeState.transportActiveIndex) || 0), Math.max(0, count - 1));

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.emitTransportSeek(Math.max(0, index - 1));
          return;
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.emitTransportSeek(Math.min(Math.max(0, count - 1), index + 1));
          return;
        }

        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          this.emitTransportToggle();
        }
        return;
      }

      if (!this.activeState.navEnabled) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        if (!this.activeState.navPrevDisabled) {
          this.emitNavigate('prev');
        }
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        if (!this.activeState.navNextDisabled) {
          this.emitNavigate('next');
        }
      }
    }

    findModalTemplate(sourceEl, modalKey) {
      if (!sourceEl || !modalKey) {
        return null;
      }

      const normalizedKey = normalizeModalKey(modalKey);
      if (!normalizedKey) {
        return null;
      }

      const templates = Array.from(sourceEl.querySelectorAll('template[data-qss-modal-key]'));
      return templates.find((template) => normalizeModalKey(template.dataset.qssModalKey || '') === normalizedKey) || null;
    }

    fragmentHasRenderableContent(fragment) {
      const probe = document.createElement('div');
      probe.appendChild(fragment.cloneNode(true));
      const hasMeaningfulText = probe.textContent && probe.textContent.trim().length > 0;
      const hasRenderableNode = Boolean(probe.querySelector('img,picture,video,svg,figure,section,article,table,ul,ol,iframe,object,embed'));
      return hasMeaningfulText || hasRenderableNode;
    }

    openModal() {
      if (!this.activeState) {
        return;
      }

      const sourceEl = this.activeState.sourceEl || document.querySelector(`[data-qss-source-id="${escapeSelectorValue(this.activeState.sourceId)}"]`);
      if (!sourceEl) {
        return;
      }

      const template = this.findModalTemplate(sourceEl, this.activeState.modalKey);
      if (!template) {
        return;
      }

      const contentFragment = template.content.cloneNode(true);
      if (!this.fragmentHasRenderableContent(contentFragment)) {
        return;
      }

      const hasPageTemplateFlag = String(template.dataset.qssModalHasPage || '').toLowerCase() === 'true';
      const hasPageContent = hasPageTemplateFlag || Boolean(contentFragment.querySelector('.qga-modal-block--page'));
      this.modalBody.innerHTML = '';
      this.modalBody.classList.toggle('has-page-content', hasPageContent);
      this.modalBody.appendChild(contentFragment);
      this.modalBody.scrollTop = 0;

      this.lastFocusedElement = document.activeElement;
      this.modalOpen = true;

      const isAlreadyOpen = typeof this.modal.open === 'boolean'
        ? this.modal.open
        : this.modal.hasAttribute('open');

      if (isAlreadyOpen) {
        this.modalClose.focus({ preventScroll: true });
        return;
      }

      const showPromise = typeof this.modal.show === 'function'
        ? this.modal.show(!this.reducedMotion)
        : Promise.resolve().then(() => {
          this.modal.setAttribute('open', this.reducedMotion ? 'immediate' : '');
        });

      Promise.resolve(showPromise).then(() => {
        if (this.modalOpen) {
          this.modalClose.focus({ preventScroll: true });
        }
      }).catch(() => {
      });
    }

    closeModal() {
      const isOpen = typeof this.modal.open === 'boolean'
        ? this.modal.open
        : this.modal.hasAttribute('open');

      if (!this.modalOpen && !isOpen) {
        return;
      }

      this.modalOpen = false;
      const hidePromise = typeof this.modal.hide === 'function'
        ? this.modal.hide()
        : Promise.resolve().then(() => {
          this.modal.removeAttribute('open');
          this.handleModalAfterHide();
        });

      Promise.resolve(hidePromise).catch(() => {
      });
    }

    handleModalAfterHide() {
      this.modalOpen = false;
      this.modalBody.innerHTML = '';
      this.modalBody.classList.remove('has-page-content');
      if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
        this.lastFocusedElement.focus({ preventScroll: true });
      }
      this.lastFocusedElement = null;
    }

    handleModalClick(event) {
      const closeTrigger = event.target.closest('[data-qga-close]');
      if (!closeTrigger) {
        return;
      }

      this.closeModal();
    }

    scheduleMetrics() {
      if (this.metricsRaf !== null) {
        return;
      }

      this.metricsRaf = window.requestAnimationFrame(() => {
        this.metricsRaf = null;
        this.emitMetrics();
      });
    }

    emitMetrics() {
      const rect = this.button.getBoundingClientRect();
      const visible = this.isVisible && !this.root.hidden && this.root.classList.contains('is-visible');
      const bottom = parseFloat(getComputedStyle(this.root).bottom || '0') || 0;
      const payload = {
        sourceId: this.activeState ? this.activeState.sourceId : '',
        visible,
        height: Math.max(0, Math.round(rect.height || 0)),
        bottom: Math.max(0, Math.round(bottom)),
        docked: this.isDocked
      };

      const signature = JSON.stringify([payload.sourceId, payload.visible, payload.height, payload.bottom, payload.docked]);
      if (signature === this.metricsSignature) {
        return;
      }

      this.metricsSignature = signature;
      document.dispatchEvent(new CustomEvent('quark:aap:metrics', { detail: payload }));
    }
  }

  window.__quarkGlobalAAP = new QuarkGlobalAAP();
})();
